import React, { useEffect, useState } from 'react';
import { communicationsService } from '../../services/api';
import { mensagemDoErroDeCarga } from '../../design';

type Amostra = { clientId: number; email: string; name?: string };
type Montada = { clientId: number; clientName: string; recipientEmail: string; subject: string; html: string };

/**
 * A PRÉVIA DO COMUNICADO (g1 + obs. do Heitor de 27/10/2026: aberta por padrão, sem botão). Quem monta é o SERVIDOR,
 * pelo mesmo render do envio (POST /communications/preview): a prévia que a tela montava sozinha não fazia
 * {{clientCnpj}}, e o e-mail que saía era outro. Até 3 destinatários reais, um de cada vez.
 *
 * O HTML vai num iframe isolado (sandbox): o e-mail traz o próprio <style>, que dentro da página mudaria a tela do HUB.
 */
export default function PreviaDoComunicado({ assunto, corpo, amostras, titulo = 'Prévia', atrasoMs = 500 }: {
  assunto: string;
  corpo: string;
  amostras: Amostra[];
  titulo?: string;
  atrasoMs?: number;
}) {
  const [montadas, setMontadas] = useState<Montada[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [lendo, setLendo] = useState(false);
  const [i, setI] = useState(0);
  const chave = JSON.stringify([assunto, corpo, amostras.slice(0, 3).map((a) => [a.clientId, a.email])]);

  useEffect(() => {
    if (!corpo.trim() && !assunto.trim()) { setMontadas(null); setErro(null); return; }
    let vivo = true;
    // Espera a digitação parar: cada tecla seria uma ida ao servidor.
    const t = setTimeout(async () => {
      setLendo(true);
      try {
        const r = await communicationsService.preview({ subject: assunto, htmlContent: corpo, recipients: amostras.slice(0, 3) });
        if (!vivo) return;
        setMontadas(Array.isArray(r) ? r : []);
        setErro(null);
        setI(0);
      } catch (e) {
        if (vivo) setErro(mensagemDoErroDeCarga(e));
      }
      if (vivo) setLendo(false);
    }, atrasoMs);
    return () => { vivo = false; clearTimeout(t); };
  }, [chave]); // eslint-disable-line react-hooks/exhaustive-deps

  const atual = montadas?.[Math.min(i, (montadas?.length || 1) - 1)];
  return (
    <div className="rounded-lg border border-central-borda bg-central-cartao p-3 flex flex-col gap-2 min-h-[320px]" data-previa-do-comunicado>
      <div className="flex items-center justify-between gap-2 text-xs text-slate-400">
        <span>
          {titulo}
          {atual && <> · como <b className="text-slate-200">{atual.clientName}</b> <span className="text-slate-500">({atual.recipientEmail})</span></>}
          {lendo && <span className="ml-2 text-slate-500">montando…</span>}
        </span>
        {montadas && montadas.length > 1 && (
          <span className="flex items-center gap-1" data-navegacao-da-amostra>
            <button type="button" aria-label="Amostra anterior" onClick={() => setI((v) => (v + montadas.length - 1) % montadas.length)} className="px-2 py-0.5 rounded border border-central-borda hover:bg-central-elevado">‹</button>
            <span>{Math.min(i, montadas.length - 1) + 1} de {montadas.length}</span>
            <button type="button" aria-label="Próxima amostra" onClick={() => setI((v) => (v + 1) % montadas.length)} className="px-2 py-0.5 rounded border border-central-borda hover:bg-central-elevado">›</button>
          </span>
        )}
      </div>
      {erro && (
        <div role="alert" className="text-xs text-red-300" data-previa="erro">
          Não consegui montar a prévia pelo servidor ({erro}). O envio não depende dela, mas confira o texto antes.
        </div>
      )}
      {!erro && !montadas && !lendo && <div className="text-xs text-slate-500">Escreva o assunto e o texto: a prévia aparece aqui.</div>}
      {atual && (
        <>
          <div className="text-sm text-central-texto font-medium break-words" data-assunto-montado>{atual.subject}</div>
          <iframe title="Prévia do e-mail" sandbox="" srcDoc={atual.html} className="w-full flex-1 min-h-[560px] rounded bg-white" data-corpo-montado />
        </>
      )}
      {montadas && montadas.length === 0 && !erro && <div className="text-xs text-slate-500">O servidor não devolveu amostra para estes destinatários.</div>}
    </div>
  );
}
